import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Table2, FlaskConical, Database, FileText, PackageCheck,
  BookOpen, Menu, X, Activity, Settings, LineChart, TestTubes, LogOut, History, KeyRound, Boxes,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { usePeriod } from '../lib/period';
import { ThemeToggleButton } from './ThemeToggle';
import { MONTHS_ID } from '../lib/api';
import { useAuth } from '../App';
import { toast } from 'sonner';

// Import Floating Chatbot Component (Gemini AI Assistant)
import ChatWidget from './ChatWidget';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, slug: 'dashboard', end: true },
  { to: '/analitik', label: 'Analitik', icon: LineChart, slug: 'analitik' },
  { to: '/pemantauan', label: 'Pemantauan Stock', icon: Table2, slug: 'pemantauan' },
  { to: '/master-reagen', label: 'Master Reagen', icon: FlaskConical, slug: 'master-reagen' },
  { to: '/database-stok', label: 'Database Stok', icon: Boxes, slug: 'database-stok' },
  { to: '/data-lis', label: 'Data LIS', icon: Database, slug: 'data-lis' },
  { to: '/prf', label: 'Purchase Request', icon: FileText, slug: 'prf' },
  { to: '/penerimaan', label: 'Penerimaan', icon: PackageCheck, slug: 'penerimaan' },
  { to: '/analisis-excel', label: 'Analisis Struktur Excel', icon: BookOpen, slug: 'analisis-excel' },
  { to: '/riwayat', label: 'Riwayat Aktivitas', icon: History, slug: 'riwayat' },
  { to: '/pengaturan', label: 'Pengaturan', icon: Settings, slug: 'pengaturan' },
];

const PAGE_TITLES = {
  '/': 'Dashboard',
  '/database-stok': 'Database Stok Reagen (FEFO)',
  '/analitik': 'Analitik Pemakaian Reagen',
  '/pemantauan': 'Pemantauan Stock',
  '/master-reagen': 'Master Reagen',
  '/data-lis': 'Data LIS',
  '/prf': 'Purchase Request',
  '/penerimaan': 'Penerimaan Barang',
  '/analisis-excel': 'Analisis Struktur Excel',
  '/riwayat': 'Riwayat Aktivitas Sistem',
  '/pengaturan': 'Pengaturan',
};

const SidebarContent = ({ onNavigate, collapsed, onToggleCollapse }) => {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 px-3 h-14 border-b">
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-400 to-blue-600 text-white shadow-md border border-teal-500/30">
            <TestTubes className="h-5 w-5" />
          </div>
          {!collapsed && (
            <div className="leading-tight truncate">
              {/* Baris 1: Reagent Management */}
              <div className="text-sm font-bold tracking-tight truncate text-foreground">
                Reagent Management
              </div>
              {/* Baris 2: System */}
              <div className="text-sm font-bold tracking-tight truncate text-foreground">
                System
              </div>
            </div>
          )}
        </div>

        {/* Tombol Toggle Sembunyikan/Munculkan Sidebar */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
            title={collapsed ? 'Buka Sidebar' : 'Sembunyikan Sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Bagian nav dan footer tetap sama seperti sebelumnya... */}

      <nav className="flex-1 space-y-1 p-2 overflow-y-auto" data-testid="app-sidebar">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`sidebar-nav-item-${item.slug}`}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                } ${collapsed ? 'justify-center px-0' : ''}`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Bagian Bawah Sidebar */}
      {!collapsed ? (
        <div className="mt-auto border-t p-4">
          <div className="flex items-center gap-1 text-[12px] text-muted-foreground">
            <Activity className="h-3 w-3 shrink-0" /> © Raihan Ghalluh Rakhasiwi 2026
          </div>
          <div className="mt-1 text-[11px] font-medium text-slate-400">
            Reagent Management System v1.3
          </div>
        </div>
      ) : (
        <div className="mt-auto border-t p-2 text-center text-[10px] text-muted-foreground">
          v1.3
        </div>
      )}
    </div>
  );
};

const PeriodSelector = () => {
  const { periods, year, month, setYear, setMonth } = usePeriod();
  const currentYear = new Date().getFullYear();
  const yearSet = new Set(periods.map((p) => p.year));
  yearSet.add(currentYear);
  yearSet.add(year);
  const yearList = Array.from(yearSet).sort((a, b) => b - a);
  return (
    <div className="flex items-center gap-2">
      <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
        <SelectTrigger className="h-9 w-[130px]" data-testid="period-month-select">
          <SelectValue placeholder="Bulan" />
        </SelectTrigger>
        <SelectContent>
          {MONTHS_ID.slice(1).map((name, idx) => (
            <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
        <SelectTrigger className="h-9 w-[100px]" data-testid="period-year-select">
          <SelectValue placeholder="Tahun" />
        </SelectTrigger>
        <SelectContent>
          {yearList.map((y) => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export const AppShell = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loadingPass, setLoadingPass] = useState(false);

  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] || 'Reagent Management System';
  const { user, logout } = useAuth();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword) {
      toast.error('Semua kolom password harus diisi');
      return;
    }
    setLoadingPass(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.username,
          old_password: oldPassword,
          new_password: newPassword
        })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password berhasil diubah! Silakan gunakan password baru.');
        setShowPasswordModal(false);
        setOldPassword('');
        setNewPassword('');
      } else {
        toast.error(data.detail || 'Gagal mengubah password');
      }
    } catch (err) {
      toast.error('Terjadi kesalahan koneksi server');
    } finally {
      setLoadingPass(false);
    }
  };

  return (
    <div className="App min-h-screen bg-background text-foreground relative">
      {/* Desktop sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden border-r bg-card lg:block transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-[260px]'
        }`}
      >
        <SidebarContent
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(!collapsed)}
        />
      </aside>

      <div className={`transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-[260px]'}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b bg-background/85 px-3 backdrop-blur sm:px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border" data-testid="mobile-menu-button">
                  {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[260px] p-0">
                <SidebarContent onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <h1 className="text-base font-semibold tracking-tight sm:text-lg" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <PeriodSelector />
            <ThemeToggleButton />
            
            {/* Profil User & Tombol Ganti Password di Sudut Kanan Atas */}
            {user && (
              <div className="flex items-center gap-2.5 pl-2 border-l">
                <div className="flex flex-col text-right">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{user.username}</span>
                  <span className="text-[10px] font-medium text-teal-600 dark:text-teal-400">{user.role}</span>
                </div>
                <button 
                  onClick={() => setShowPasswordModal(true)} 
                  title="Ganti Password"
                  className="rounded-lg border border-slate-200 p-1.5 text-xs text-teal-600 hover:bg-teal-50 dark:border-slate-800 dark:hover:bg-teal-950/30 transition-colors"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
                <button 
                  onClick={logout} 
                  title="Keluar Akun"
                  className="rounded-lg border border-slate-200 p-1.5 text-xs text-red-500 hover:bg-red-50 dark:border-slate-800 dark:hover:bg-red-950/30 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="px-3 py-4 sm:px-4 lg:px-6">{children}</main>
      </div>

      {/* Floating Chatbot Widget (Gemini AI Assistant) */}
      <ChatWidget />

      {/* Modal Ganti Password */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl border dark:border-slate-800 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-base font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                <KeyRound className="h-5 w-5 text-teal-600" /> Ganti Password Akun
              </h3>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4 text-xs">
              <div>
                <label className="block font-medium mb-1 text-slate-600 dark:text-slate-300">Password Lama</label>
                <input 
                  type="password" 
                  value={oldPassword} 
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Masukkan password saat ini"
                  className="w-full rounded-md border px-3 py-2 bg-background text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block font-medium mb-1 text-slate-600 dark:text-slate-300">Password Baru</label>
                <input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Masukkan password baru"
                  className="w-full rounded-md border px-3 py-2 bg-background text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="rounded-lg border px-4 py-2 font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={loadingPass}
                  className="rounded-lg bg-teal-600 px-4 py-2 font-medium text-white shadow hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  {loadingPass ? 'Menyimpan...' : 'Simpan Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
